import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useRedeemInvite } from "./use-redeem-invite";
import type { UserRole } from "@colab/shared";

type LookupResponse = {
  email: string;
  role: UserRole;
  partnerId: string | null;
  expiresAt: string;
  consumedAt: string | null;
};

const schema = z.object({
  password: z.string().optional(),
});
type RedeemForm = z.infer<typeof schema>;

// Replaces /app/(auth)/invite/[token]/page.tsx + InviteRedeemForm.tsx. The
// SPA can't hold the service-role key, so the invite read happens on
// /api/invites/lookup and the redemption on /api/invites/redeem.
export function InvitePage() {
  const { token = "" } = useParams<{ token: string }>();

  const lookup = useQuery<LookupResponse, ApiClientError>({
    queryKey: ["invites", "lookup", token],
    queryFn: () =>
      apiFetch<LookupResponse>("/invites/lookup", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  });

  if (lookup.isLoading) {
    return (
      <div className="rounded-xl border bg-card p-8 text-sm text-muted-foreground shadow-sm">
        Checking invitation…
      </div>
    );
  }

  if (lookup.isError) {
    const code = lookup.error.code;
    if (code === "not_found") {
      return <InvalidInvite />;
    }
    if (code === "consumed") {
      return (
        <ExpiredOrConsumed
          title="Invitation already used"
          body="This invite has already been redeemed. Sign in instead, or ask the Bookboost partnerships team for a fresh one."
        />
      );
    }
    if (code === "expired") {
      return (
        <ExpiredOrConsumed
          title="Invitation expired"
          body="Ask the Bookboost partnerships team to send a fresh invitation."
        />
      );
    }
    return (
      <ExpiredOrConsumed
        title="Couldn't load invitation"
        body={lookup.error.message || "Please try again in a moment."}
      />
    );
  }

  if (!lookup.data) return null;
  return <InviteFormCard token={token} invite={lookup.data} />;
}

function InvalidInvite() {
  return (
    <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
      <h1 className="font-display text-2xl font-semibold tracking-tight">
        Invitation not found
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This invite link is invalid. Ask the Bookboost partnerships team to send a new one.
      </p>
    </div>
  );
}

function ExpiredOrConsumed({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
      <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function InviteFormCard({ token, invite }: { token: string; invite: LookupResponse }) {
  const [sent, setSent] = useState(false);
  const redeem = useRedeemInvite();

  const form = useForm<RedeemForm>({
    resolver: zodResolver(schema),
    defaultValues: { password: "" },
  });

  useEffect(() => {
    if (invite.role === "internal") {
      form.register("password", {
        required: "Choose a password.",
        minLength: { value: 8, message: "At least 8 characters." },
      });
    }
  }, [invite.role, form]);

  async function onSubmit(values: RedeemForm) {
    try {
      await redeem.mutateAsync({
        token,
        password: invite.role === "internal" ? values.password : undefined,
      });
    } catch (error) {
      const msg =
        error instanceof ApiClientError ? error.message : "Failed to accept invitation.";
      toast.error(msg);
      return;
    }

    if (invite.role === "internal" && values.password) {
      // Auto-sign-in: the worker created the auth user with this password.
      const { error } = await supabase.auth.signInWithPassword({
        email: invite.email,
        password: values.password,
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Account created.");
      window.location.href = "/admin/dashboard";
      return;
    }

    setSent(true);
  }

  if (sent) {
    return (
      <div className="rounded-xl border bg-card p-8 shadow-sm">
        <Alert className="border-accent/30 bg-accent/5">
          <AlertDescription>
            <div className="font-medium text-foreground">Magic link sent</div>
            <p className="mt-1 text-muted-foreground">
              Check <span className="font-medium text-foreground">{invite.email}</span> for a
              sign-in link. After signing in you&apos;ll be asked to complete your profile.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-8 shadow-sm">
      <div className="mb-6 space-y-1.5">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome to Colab</h1>
        <p className="text-sm text-muted-foreground">
          You&apos;re invited as a{" "}
          <span className="font-medium text-foreground">{invite.role}</span>. We&apos;ll email{" "}
          <span className="font-medium text-foreground">{invite.email}</span>{" "}
          {invite.role === "internal" ? "once you set a password." : "a magic link to finish setup."}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormItem>
            <FormLabel>Email</FormLabel>
            <Input type="email" value={invite.email} disabled className="bg-muted/40" />
          </FormItem>

          {invite.role === "internal" && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Choose a password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormDescription>At least 8 characters.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Button type="submit" disabled={redeem.isPending} className="w-full">
            {redeem.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {invite.role === "internal" ? "Create account" : "Send magic link"}
          </Button>
        </form>
      </Form>
    </div>
  );
}
